import { browser } from "$app/environment";

import { get, type Updater, type Writable, writable } from "svelte/store";

const storeCache: { [key: string]: Writable<any> } = {};

function localStoreUtil<T>(key: string, init: (data?: any) => T): Writable<T> {
  if (storeCache[key]) {
    return storeCache[key];
  }

  function writeToStorage(value: T) {
    if (browser) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  function readFromStorage(): T {
    if (!browser) return init();

    const json = localStorage.getItem(key);
    if (json === null) return init();

    try {
      return init(JSON.parse(json));
    } catch (e) {
      console.error(`Error parsing local storage value for key ${key}:`, e);
      localStorage.removeItem(key);
      return init();
    }
  }

  const value = readFromStorage();
  const baseStore = writable(value);
  const { subscribe, set, update } = baseStore;

  // Create a new store which wraps baseStore, and saves the value to localStorage
  const localStore = {
    subscribe,
    set(value: T) {
      set(value);
      writeToStorage(value);
    },
    update(callback: Updater<T>) {
      update(callback);
      writeToStorage(get(baseStore));
    },
  };

  storeCache[key] = localStore;
  return localStore;
}

/** Track a value that is saved in the browser's localStorage. */
export function getLocalStore<T>(key: string, defaultValue: T): Writable<T> {
  return localStoreUtil(key, (value) => value ?? defaultValue);
}

/**
 * Track a shallow object with defaults saved in localStorage.
 *
 * Unlike `getLocalStore`, this function will merge any values with new defaults
 * into the existing stored object. This allows you to add new default values
 * to the store without needing to write a migration.
 *
 * For instance, if you had `{ foo: 1 }` stored, and you updated the type to
 * also have `bar` with a different default, `bar` would be merged into the
 * stored object on new versions of the page.
 */
export function getLocalShallowMerge<T extends Record<string, any>>(
  key: string,
  defaultValues: T,
): Writable<T> {
  return localStoreUtil(key, (value) => {
    if (value == undefined || typeof value !== "object") return defaultValues;
    return { ...defaultValues, ...value };
  });
}
